import React from "react";
import { baseURL } from "../../config/APIConfig";

/* -------------------- SAFE JSON PARSER (RECURSIVE) -------------------- */
const tryParseJSON = (value) => {
    if (typeof value !== "string") return value;

    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

/* -------------------- NORMALIZER (handles nested JSON strings) -------------------- */
const normalizeData = (input) => {
    if (typeof input === "string") {
        const parsed = tryParseJSON(input);
        if (parsed !== input) return normalizeData(parsed);
        return input;
    }

    if (Array.isArray(input)) {
        return input.map(normalizeData);
    }

    if (typeof input === "object" && input !== null) {
        const result = {};
        Object.entries(input).forEach(([k, v]) => {
            result[k] = normalizeData(v);
        });
        return result;
    }

    return input;
};

/* -------------------- VALUE RENDERER -------------------- */
const renderValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    const parsedValue = normalizeData(value);

    if (Array.isArray(parsedValue)) {
        return parsedValue.map((item, i) => (
            <div key={i} className="ml-2 border-l pl-2 space-y-1">
                {typeof item === "object" ? (
                    <EmailFields selectedEmail={{ extractedFields: { data: item } }} />
                ) : (
                    <span>{String(item)}</span>
                )}
            </div>
        ));
    }

    if (typeof parsedValue === "object") {
        return (
            <EmailFields selectedEmail={{ extractedFields: { data: parsedValue } }} />
        );
    }

    return String(parsedValue);
};

const PDF_FILE_PATH_KEYS = [
    "FilePath",
    "filePath",
    "file_path",
    "fileUrl",
    "file_url",
    "pdfUrl",
    "pdfPath",
    "attachmentPath",
];

const PDF_BASE64_KEYS = ["pdfBase64", "PdfBase64", "pdf_base64"];

const isNonEmptyString = (value) =>
    typeof value === "string" && value.trim().length > 0;

const looksLikePdfPath = (value) => {
    if (!isNonEmptyString(value)) return false;
    const trimmed = value.trim();
    return (
        /^https?:\/\//i.test(trimmed) ||
        trimmed.startsWith("/") ||
        /\.pdf($|\?|#)/i.test(trimmed)
    );
};

const looksLikeBase64Pdf = (value) => {
    if (!isNonEmptyString(value)) return false;
    const trimmed = value.trim().replace(/^data:application\/pdf;base64,/, "");
    return trimmed.length > 100 && /^[A-Za-z0-9+/=\s]+$/.test(trimmed);
};

const toPdfDataUrl = (base64) => {
    const clean = String(base64)
        .trim()
        .replace(/^data:application\/pdf;base64,/, "");
    return `data:application/pdf;base64,${clean}`;
};

const resolveAbsolutePdfUrl = (path) => {
    const trimmed = String(path).trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (trimmed.startsWith("//")) return `https:${trimmed}`;
    const base = (baseURL?.AiBaseURL || "https://pyapi.shipez.ai").replace(/\/$/, "");
    return `${base}/${trimmed.replace(/^\//, "")}`;
};

/** Booking Agent: base64 from email payload or extractedFields.data */
export const getBookingPdfBase64 = (selectedEmail) => {
    if (looksLikeBase64Pdf(selectedEmail?.pdfBase64)) {
        return selectedEmail.pdfBase64.trim();
    }

    const rawData =
        selectedEmail?.extractedFields?.data ||
        selectedEmail?.extractedFields ||
        {};
    const data = normalizeData(rawData);

    if (typeof data !== "object" || data === null || Array.isArray(data)) {
        return null;
    }

    for (const key of PDF_BASE64_KEYS) {
        const value = data[key];
        if (looksLikeBase64Pdf(value)) return value.trim();
    }

    return null;
};

/** Booking Agent: file URL/path from extractedFields.data (skipped when base64 exists) */
export const getBookingPdfFileUrl = (selectedEmail) => {
    if (getBookingPdfBase64(selectedEmail)) return null;

    const rawData =
        selectedEmail?.extractedFields?.data ||
        selectedEmail?.extractedFields ||
        {};
    const data = normalizeData(rawData);

    if (typeof data === "string" && looksLikePdfPath(data)) {
        return resolveAbsolutePdfUrl(data);
    }

    if (typeof data !== "object" || data === null || Array.isArray(data)) {
        return null;
    }

    for (const key of PDF_FILE_PATH_KEYS) {
        const value = data[key];
        if (looksLikePdfPath(value)) return resolveAbsolutePdfUrl(value);
    }

    return null;
};

/** iframe src: base64 data URL first, then file URL, otherwise null */
export const getBookingPdfPreviewSrc = (selectedEmail) => {
    const base64 = getBookingPdfBase64(selectedEmail);
    if (base64) return toPdfDataUrl(base64);

    const fileUrl = getBookingPdfFileUrl(selectedEmail);
    if (fileUrl) return fileUrl;

    return null;
};

export const hasBookingPdfPreview = (selectedEmail) =>
    Boolean(getBookingPdfPreviewSrc(selectedEmail));

/* -------------------- MAIN COMPONENT -------------------- */
export default function EmailFields({ selectedEmail }) {
    const rawData =
        selectedEmail?.extractedFields?.data ||
        selectedEmail?.extractedFields ||
        {};

    const data = normalizeData(rawData);

    return (
        <div className="max-h-[60vh] overflow-auto rounded-lg border border-slate-200 dark:border-white/10 shadow-sm bg-white dark:bg-slate-900">
            <table className="w-full text-sm text-left">
                <tbody>
                    {Object.entries(data).map(([key, value]) => {
                        if (value === null || value === undefined || value === "") return null;

                        return (
                            <tr
                                key={key}
                                className="group border-b border-slate-100 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition"
                            >
                                {/* KEY COLUMN */}
                                <td className="w-1/3 px-4 py-3 bg-slate-50/70 dark:bg-slate-800/40 align-top">
                                    <div className="flex items-start gap-2">
                                        <div className="w-1 h-full bg-blue-500 rounded opacity-70 group-hover:opacity-100" />
                                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 tracking-wide">
                                            {key
                                                .replace(/([A-Z])/g, " $1")
                                                .replace(/^./, (str) => str.toUpperCase())
                                                .trim()}
                                        </span>
                                    </div>
                                </td>

                                {/* VALUE COLUMN */}
                                <td className="w-2/3 px-4 py-3 text-slate-700 dark:text-white align-top">
                                    <div className="leading-relaxed break-words">
                                        {renderValue(value)}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}